import { useEffect, useRef, type RefObject } from 'react';
import { Platform } from 'react-native';
import { useIsMobileWeb } from '@/hooks/useIsMobileWeb';

/**
 * Schedules a focus callback with platform-appropriate timing.
 * On native, delays 50ms so the view is mounted and the container animation has
 * started settling — Android's InputMethodManager won't reliably show the keyboard
 * if focus fires while the view hierarchy is still in flux (e.g. mid-Actionsheet
 * slide-in). If the keyboard still fails to appear on slower devices, increase this.
 * On web, delays 300ms so modal/drawer CSS transitions settle before focusing.
 * Skipped on mobile web to avoid triggering the virtual keyboard on sheet open.
 * @param focus Callback that performs the actual focus
 * @param enabled Whether to auto-focus (default: true)
 */
export function useAutoFocusEffect(focus: () => void, enabled = true) {
  const isMobileWeb = useIsMobileWeb();
  useEffect(() => {
    if (!enabled || isMobileWeb) return;
    const delay = Platform.OS === 'web' ? 300 : 50;
    const timer = setTimeout(focus, delay);
    return () => clearTimeout(timer);
  }, [enabled, isMobileWeb]);
}

/**
 * Returns a ref that auto-focuses with platform-appropriate timing.
 * Convenience wrapper around useAutoFocusEffect for simple ref-based usage.
 * @param enabled Whether to auto-focus (default: true)
 */
export function useDelayedAutoFocus(enabled = true): RefObject<any> {
  const ref = useRef<any>(null);
  useAutoFocusEffect(() => ref.current?.focus(), enabled);
  return ref;
}
